<script setup lang="ts">
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogClose,
} from 'radix-vue'

defineProps<{
  open: boolean
  title?: string
  maxWidth?: string
  bodyClass?: string
}>()

defineEmits<{
  'update:open': [value: boolean]
}>()
</script>

<template>
  <DialogRoot :open="open" @update:open="$emit('update:open', $event)">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-50 bg-black/75 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
      <DialogContent
        :class="[
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100vw-2rem)] bg-surface-container border border-border rounded-lg shadow-2xl focus:outline-none',
          'max-h-[90vh] flex flex-col',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          maxWidth ?? 'sm:max-w-lg',
        ]"
      >
        <!-- Header -->
        <div class="flex items-start justify-between px-6 pt-6 pb-5 shrink-0">
          <DialogTitle
            v-if="title"
            class="font-headline font-black text-lg uppercase tracking-tight text-on-surface"
          >
            {{ title }}
          </DialogTitle>
          <DialogTitle v-else class="flex-1 min-w-0">
            <slot name="title" />
          </DialogTitle>
          <DialogClose
            class="ml-auto -mt-1 -mr-1 p-1 text-outline hover:text-on-surface hover:bg-surface-container-high transition-colors"
            aria-label="Close"
          >
            <span class="material-symbols-outlined text-xl">close</span>
          </DialogClose>
        </div>

        <!-- Scrollable body -->
        <div :class="['px-6 pb-6 flex-1 min-h-0', bodyClass ?? 'overflow-y-auto']">
          <slot />

          <div v-if="$slots.footer" class="mt-6 flex justify-end gap-2">
            <slot name="footer" />
          </div>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
